import React from "react";
import { ButtonArrow } from "@thrivetribe/mvf.assets.icons.button-arrow";
import { Button } from "@thrivetribe/mvf.ui.atoms.button";
import { Body, BodyContent, BodyHeading } from "@thrivetribe/mvf.ui.organisms.body";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import { useGetRecipeQuery, useGetRandomRecipesQuery } from "redux/contentApiSlice";
import { RecipeBanner } from "@thrivetribe/mvf.ui.molecules.recipe-banner";
import { MarkedDownContent } from "@thrivetribe/mvf.ui.atoms.marked-down-content";
import { Tab } from "types/tab";
import { Tabs } from "@thrivetribe/mvf.ui.molecules.tabs";
import { RecipeTips } from "@thrivetribe/mvf.ui.molecules.recipe-tips";
import { RecipeThreeCards } from "ui/molecules/RecipeThreeCards";
import "./RecipePage.scss";
import { WhatIsInAServing } from "./WhatIsInAServing";
import RtkErrorMessage from "ui/organisms/Errors/RtkErrorMessage";
import { LoadingSpinner } from "@thrivetribe/mvf.ui.molecules.loading-spinner";

export const RecipePage: React.FC = () => {
  const navigate = useNavigate();
  let { slug } = useParams();

  if (!slug) {
    throw new Error(`Unexpected rendering of the article page without slug parameter`);
  }

  const { data: recipe, isLoading: isRecipeLoading, isError, error } = useGetRecipeQuery(slug);

  const mealParams: any = {};
  const mealType = recipe?.mealType[0];
  if (mealType) {
    mealParams[mealType.toLowerCase()] = 3;
  }
  const { data: recipes , isLoading: isRecipesLoading } = useGetRandomRecipesQuery(mealParams);

  const uniqueRecipes = recipes?.filter((x) => x.slug !== recipe?.slug).slice(0, 2);

  const tabs: Tab[] = [
    {
      title: "Ingredients",
      children: <MarkedDownContent content={recipe?.ingredients} />,
    },
    {
      title: "Method",
      children: <MarkedDownContent content={recipe?.method} />,
    },
  ];

  if(isRecipeLoading || isRecipesLoading) {
    return <LoadingSpinner fullscreen={false} />    
  }

  return (
    isError ?  ( 
        <RtkErrorMessage error={error} /> 
    ) : (
    <Body className="recipe">
      {recipe && (
        <Helmet>
          <title>{`${recipe?.title} - MAN v FAT`}</title>
        </Helmet>
      )}
      <BodyHeading>
        <Button variant="text" onClick={() => navigate(-1)}>
          <ButtonArrow />
          Back
        </Button>
        <h1>{recipe?.title}</h1>
      </BodyHeading>

      <BodyContent>
        {recipe && (
          <RecipeBanner
            mealType={recipe.mealType[0]}
            time={recipe.readyIn}
            imgUrl={recipe?.asset.file.url}
            title="Why you'll love this recipe"
            description={recipe?.introduction}
            healthBenefit={recipe.healthBenefit}
            servings={recipe?.servings}
            kCal={recipe.nutritionalBreakdown?.portion?.singleServing.foodLabel.energyKcal.weight}
          />
        )}
        {recipe && (
          <div className="recipe__content">
            <div className="recipe__content--desktop">
              <div className="recipe__content--left-grid">
                <div className="recipe__content--method">
                  <Tabs
                    tabs={[
                      {
                        title: "Method",
                        children: <MarkedDownContent content={recipe?.method} />,
                      },
                    ]}
                  />
                </div>
                <div className="receipe__portions--tips">
                  <RecipeTips>
                    <h2>Tips and tweaks</h2>
                    <p>{recipe?.tipsAndTweaks}</p>
                  </RecipeTips>
                </div>
              </div>
              <div className="recipe__content--right-grid">
                <div className="recipe__content--ingredients">
                  <Tabs
                    tabs={[
                      {
                        title: "Ingredients",
                        children: <MarkedDownContent content={recipe?.ingredients} />,
                      },
                    ]}
                  />
                </div>
                <WhatIsInAServing recipe={recipe} />
              </div>
            </div>
            <div className="recipe__content--upto-desktop">
              <Tabs tabs={tabs} />
              <div className="recipe__portions">
                <div className="receipe__portions--tips">
                  <RecipeTips>
                    <h2>Tips and tweaks</h2>
                    <p>{recipe?.tipsAndTweaks}</p>
                  </RecipeTips>
                </div>
                <WhatIsInAServing recipe={recipe} />
              </div>
            </div>
            <div className="spacer" />
            <RecipeThreeCards
              title="Hungry for more?"
              description="Try another recipe, plan a few meals for the week ahead, or check out some of our articles that’ll help you find out more about football, eating well, physical activity and more."
              recipes={uniqueRecipes ?? []}
            />
          </div>
        )}
      </BodyContent>
    </Body>
  )
  );
};
